import * as React from "react";
import { cn } from "../../shared/ui/index.ts";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { I, type IconProps } from "../../shared/icons/icons.tsx";
import {
  activeNavId,
  getNavItems,
  PATIENT_MODULE_NAV,
  parsePatientRoute,
  patientModulePath,
  paths,
  usePracticeUuid,
  type NavItem as NavItemData,
  type PatientModuleId,
} from "../services/navigation.service.ts";
import {
  addUserModule,
  applyOrder,
  setModulesOrder,
  setPatientModulesOrder,
  toggleModuleVisibility,
  togglePatientModuleVisibility,
  useModulesConfig,
} from "../services/modules-config.service.ts";
import { Interactions } from "../providers/interactions.provider.tsx";
import type { Patient } from "../services/patients.service.ts";
import { PatientAvatar } from "../../shared/patient/patient-avatar.component.tsx";
import { formatPatientDisplayName } from "../../shared/patient/patient-name.ts";
import { PatientNameDisplay } from "../../shared/patient/patient-name-display.component.tsx";
import { PatientQuickActions } from "../../shared/patient/patient-quick-actions.component.tsx";
import {
  DockingEditBarSlot,
  usePendingRename,
  useSidebarEditMode,
} from "../../shared/docking/docking-edit-bar-host.tsx";
import { appFocusPatientOnDashboard } from "../app-bridge.ts";
import { useNarrowLayout } from "../hooks/use-narrow-layout.ts";

type IconCmp = (p: IconProps) => React.ReactElement;

interface MergedModule {
  id: string;
  label: string;
  icon: IconCmp;
  badge: number | null;
  badgeColor?: "primary";
  path: string;
  editable: boolean;
  renameable: boolean;
  deletable: boolean;
}

function useMergedModules(
  practiceUuid: string,
  includeUserModules: boolean,
): MergedModule[] {
  const config = useModulesConfig();
  const navItems = getNavItems(practiceUuid);

  const fixed: MergedModule[] = navItems.map((n) => ({
    id: n.id,
    label: n.label,
    icon: n.icon as IconCmp,
    badge: n.badge,
    badgeColor: n.badgeColor,
    path: n.path,
    editable: n.id === "dashboard",
    renameable: false,
    deletable: false,
  }));

  const user: MergedModule[] = includeUserModules
    ? config.userModules.map((u) => ({
        id: u.id,
        label: u.label,
        icon: I.LayoutGrid,
        badge: null,
        path: paths.userModule(practiceUuid, u.id),
        editable: true,
        renameable: true,
        deletable: true,
      }))
    : [];

  return applyOrder([...fixed, ...user], config.order);
}

function Sidebar({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const practiceUuid = usePracticeUuid();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = activeNavId(pathname, practiceUuid);
  const patientRoute = parsePatientRoute(pathname);
  const patient = Interactions.usePatient();
  const current = patient.current;
  const config = useModulesConfig();
  const narrow = useNarrowLayout();
  const allModules = useMergedModules(practiceUuid, !narrow);
  const { sidebarEditMode: editMode, setSidebarEditMode: setEditMode } =
    useSidebarEditMode();
  const { setPendingRenameModuleId } = usePendingRename();
  const [dragId, setDragId] = React.useState<string | null>(null);

  // FLIP animation : snapshot des positions avant réorganisation, puis
  // animation des deltas en useLayoutEffect après la réorganisation.
  const rowRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const flipPendingRef = React.useRef<Map<HTMLElement, DOMRect> | null>(null);

  const registerRow = React.useCallback(
    (id: string, el: HTMLElement | null) => {
      if (el) rowRefs.current.set(id, el);
      else rowRefs.current.delete(id);
    },
    [],
  );

  const snapshotPositions = React.useCallback(() => {
    const map = new Map<HTMLElement, DOMRect>();
    rowRefs.current.forEach((el) => {
      map.set(el, el.getBoundingClientRect());
    });
    flipPendingRef.current = map;
  }, []);

  React.useLayoutEffect(() => {
    const old = flipPendingRef.current;
    if (!old) return;
    flipPendingRef.current = null;
    rowRefs.current.forEach((el, id) => {
      // L'élément en cours de drag ne doit pas être animé : son image
      // fantôme suit déjà le curseur, animer le source crée de la vibration.
      if (id === dragId) return;
      const oldRect = old.get(el);
      if (!oldRect) return;
      const newRect = el.getBoundingClientRect();
      const dy = oldRect.top - newRect.top;
      if (Math.abs(dy) < 0.5) return;
      try {
        el.animate(
          [
            { transform: `translateY(${dy}px)` },
            { transform: "translateY(0)" },
          ],
          {
            duration: 220,
            easing: "cubic-bezier(.2,.8,.2,1)",
            composite: "replace",
          },
        );
      } catch {
        /* Web Animations API optional */
      }
    });
  });

  React.useEffect(() => {
    if (collapsed) setEditMode(false);
  }, [collapsed]);

  const userModuleMatch = /\/pro\/[^/]+\/m\/([^/?]+)/.exec(pathname);
  const activeUserModuleId = userModuleMatch ? userModuleMatch[1] : null;

  const visibleModules = editMode
    ? allModules
    : allModules.filter((m) => !config.hiddenIds.includes(m.id));

  // On garde une ref vers la dernière liste de modules pour que le drag
  // pointer-events puisse lire l'ordre courant sans recréer ses listeners.
  const allModulesRef = React.useRef(allModules);
  allModulesRef.current = allModules;

  const tryReorder = React.useCallback(
    (fromId: string, overId: string, clientY: number) => {
      if (fromId === overId) return;
      const order = allModulesRef.current.map((m) => m.id);
      const fromIdx = order.indexOf(fromId);
      const toIdx = order.indexOf(overId);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const targetEl = rowRefs.current.get(overId);
      if (targetEl) {
        const rect = targetEl.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const goingDown = fromIdx < toIdx;
        if (goingDown && clientY < midY) return;
        if (!goingDown && clientY > midY) return;
      }
      snapshotPositions();
      const next = [...order];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved!);
      setModulesOrder(next);
    },
    [snapshotPositions],
  );

  const startPointerDrag = React.useCallback(
    (id: string, ev: React.PointerEvent<HTMLElement>) => {
      // Bouton souris non principal : on laisse passer le clic normal.
      if (ev.button !== 0 && ev.pointerType === "mouse") return;
      ev.preventDefault();
      setDragId(id);
      const target = ev.currentTarget;
      try {
        target.setPointerCapture(ev.pointerId);
      } catch {
        /* setPointerCapture peut échouer sur d'anciennes implémentations */
      }
      document.body.style.userSelect = "none";

      const onMove = (e: PointerEvent) => {
        for (const [rowId, rowEl] of rowRefs.current.entries()) {
          if (rowId === id) continue;
          const rect = rowEl.getBoundingClientRect();
          if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
            tryReorder(id, rowId, e.clientY);
            return;
          }
        }
      };
      const onUp = () => {
        setDragId(null);
        document.body.style.userSelect = "";
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };
      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [tryReorder],
  );

  const handleClickModule = (m: MergedModule) => {
    navigate({ to: m.path });
    // L'auto-déploiement du bandeau docking est géré côté page :
    // si Modifier est ON et le module est éditable, ApplyEditMode active
    // automatiquement editMode dans le DockingProvider.
  };

  const handleAddModule = () => {
    // Création immédiate, sans modale : on génère un nom par défaut et on
    // signale au bandeau docking de prendre le focus sur le champ Renommer.
    const baseLabel = "Nouveau module";
    const existing = new Set(allModules.map((m) => m.label));
    let label = baseLabel;
    let n = 2;
    while (existing.has(label)) {
      label = `${baseLabel} ${n}`;
      n += 1;
    }
    const mod = addUserModule(label);
    setPendingRenameModuleId(mod.id);
    navigate({ to: paths.userModule(practiceUuid, mod.id) });
    // Modifier est déjà ON ; le bandeau docking s'auto-déploie et le
    // RenameField se focus + sélectionne tout grâce à pendingRenameModuleId.
  };

  return (
    <aside className="flex h-full min-h-0 flex-col overflow-x-visible overflow-y-hidden border-r border-sf-border bg-sf-surface">
      <div className="flex min-h-0 flex-1 flex-col overflow-x-visible overflow-y-auto">
        {current && (
          <PatientCard
            patient={current}
            collapsed={collapsed}
            editMode={editMode && !collapsed}
            activeModule={patientRoute?.moduleId}
            onOpenModule={(moduleId) =>
              navigate({
                to: patientModulePath(practiceUuid, current.uuid, moduleId),
              })
            }
            onClear={() => patient.clearPatient()}
          />
        )}

        <nav
          className={cn(
            "flex flex-col gap-px overflow-visible px-2 pb-2",
            current ? "pt-1" : "pt-2.5",
          )}
        >
          {visibleModules.map((m) => {
            const isActive =
              activeUserModuleId === m.id ||
              (active === m.id && !activeUserModuleId);
            const isHidden = config.hiddenIds.includes(m.id);
            return (
              <ModuleRow
                key={m.id}
                module={m}
                active={isActive}
                collapsed={collapsed}
                editMode={editMode && !collapsed}
                isHidden={isHidden}
                dragging={dragId === m.id}
                registerRow={registerRow}
                onClick={() => handleClickModule(m)}
                onToggleHide={() => toggleModuleVisibility(m.id)}
                onPointerDrag={(ev) => startPointerDrag(m.id, ev)}
              />
            );
          })}

          {editMode && !collapsed && !narrow && (
            <button
              type="button"
              onClick={handleAddModule}
              className="mt-1 flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-lg border border-dashed border-sf-border bg-transparent px-3 text-[14px] font-medium text-sf-text-muted hover:border-sf-primary hover:text-sf-primary lg:h-8 lg:rounded-md lg:px-2.5 lg:text-[13px]"
            >
              <I.Plus size={14} />
              <span>Nouveau module</span>
            </button>
          )}
        </nav>
      </div>

      {!collapsed && (
        <div className="shrink-0 border-t border-sf-border bg-sf-surface px-2 py-2">
          <button
            type="button"
            onClick={() => setEditMode(!editMode)}
            className={cn(
              "flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border font-[inherit] text-[14px] font-medium transition-colors lg:h-9 lg:rounded-md lg:text-[12.5px]",
              editMode
                ? "border-sf-primary bg-sf-primary text-white shadow-(--sf-shadow-sm) hover:opacity-90"
                : "border-sf-border bg-sf-surface text-sf-text-secondary hover:bg-gray-50 hover:text-sf-text",
            )}
          >
            {editMode ? (
              <>
                <I.Check size={14} />
                Terminer
              </>
            ) : (
              <>
                <I.Edit size={13} />
                Modifier
              </>
            )}
          </button>
        </div>
      )}
    </aside>
  );
}

function ModuleRow({
  module,
  active,
  collapsed,
  editMode,
  isHidden,
  dragging,
  registerRow,
  onClick,
  onToggleHide,
  onPointerDrag,
}: {
  module: MergedModule;
  active: boolean;
  collapsed: boolean;
  editMode: boolean;
  isHidden: boolean;
  dragging: boolean;
  registerRow: (id: string, el: HTMLElement | null) => void;
  onClick: () => void;
  onToggleHide: () => void;
  onPointerDrag: (ev: React.PointerEvent<HTMLElement>) => void;
}) {
  const Icn = module.icon;
  const refCb = React.useCallback(
    (el: HTMLElement | null) => registerRow(module.id, el),
    [registerRow, module.id],
  );

  if (!editMode) {
    return (
      <NavItem
        item={{
          id: module.id,
          label: module.label,
          icon: Icn,
          badge: module.badge,
          badgeColor: module.badgeColor,
        }}
        active={active}
        collapsed={collapsed}
        onClick={onClick}
      />
    );
  }

  return (
    <>
      <div
        ref={refCb}
        className={cn(
          "group relative flex h-10 w-full items-center gap-0.5 rounded-lg pl-0.5 pr-1 transition-[background-color,box-shadow] touch-none lg:h-8 lg:rounded-md",
          dragging
            ? "z-10 bg-sf-primary-50 shadow-[0_4px_12px_-2px_rgba(79,70,229,0.25)] ring-1 ring-sf-primary/40"
            : active
              ? "bg-sf-primary-50"
              : "bg-transparent hover:bg-gray-50",
          !dragging && isHidden && "opacity-60",
        )}
      >
        {dragging && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-0.5 rounded-r bg-sf-primary"
          />
        )}

        <span
          onPointerDown={onPointerDrag}
          className="flex h-6 w-3.5 shrink-0 cursor-grab touch-none items-center justify-center text-sf-text-muted/70 hover:text-sf-text-secondary active:cursor-grabbing"
          title="Glisser pour réorganiser"
        >
          <I.Drag size={12} />
        </span>

        <button
          type="button"
          onClick={onClick}
          className="flex min-w-0 flex-1 items-center gap-2 border-none bg-transparent px-1 py-1 text-left font-[inherit] text-[13px] text-sf-text"
        >
          <Icn size={14} className="shrink-0 text-sf-text-secondary" />
          <span className="min-w-0 flex-1 truncate">{module.label}</span>
          {module.editable && (
            <I.LayoutGrid
              size={10}
              className="shrink-0 text-sf-primary/55"
              aria-label="Docking personnalisable"
            />
          )}
        </button>

        <button
          type="button"
          onClick={onToggleHide}
          title={isHidden ? "Afficher" : "Masquer"}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border-0 bg-transparent text-sf-text-muted hover:bg-gray-100 hover:text-sf-text"
        >
          {isHidden ? <I.EyeOff size={13} /> : <I.Eye size={13} />}
        </button>
      </div>
      {active && module.editable && (
        <DockingEditBarSlot className="empty:hidden mt-1 mb-1 px-0.5" />
      )}
    </>
  );
}

function PatientCardIdentity({ patient }: { patient: Patient }) {
  return (
    <div className="min-w-0 flex-1">
      <PatientNameDisplay name={patient.name} />
      <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px] leading-none text-sf-text-muted">
        <span className="shrink-0 font-medium text-sf-text-secondary">
          {patient.age} ans
        </span>
        {patient.tag ? (
          <>
            <span className="shrink-0 text-sf-border-strong" aria-hidden>
              ·
            </span>
            <span className="truncate">{patient.tag}</span>
          </>
        ) : null}
      </div>
    </div>
  );
}

function PatientCard({
  patient,
  collapsed,
  editMode,
  activeModule,
  onOpenModule,
  onClear,
}: {
  patient: Patient;
  collapsed: boolean;
  editMode: boolean;
  activeModule?: PatientModuleId;
  onOpenModule: (moduleId: PatientModuleId) => void;
  onClear: () => void;
}) {
  const isOnPatientModule = activeModule != null;
  const displayName = formatPatientDisplayName(patient.name);
  const config = useModulesConfig();
  const [dragId, setDragId] = React.useState<string | null>(null);
  const rowRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const flipPendingRef = React.useRef<Map<HTMLElement, DOMRect> | null>(null);

  const orderedPatientModules = React.useMemo(() => {
    const enriched = PATIENT_MODULE_NAV.map((m) => ({ ...m }));
    return applyOrder(enriched, config.patientOrder);
  }, [config.patientOrder]);

  const visiblePatientModules = editMode
    ? orderedPatientModules
    : orderedPatientModules.filter(
        (m) => !config.patientHiddenIds.includes(m.id),
      );

  const registerPatientRow = React.useCallback(
    (id: string, el: HTMLElement | null) => {
      if (el) rowRefs.current.set(id, el);
      else rowRefs.current.delete(id);
    },
    [],
  );

  const snapshotPatientPositions = React.useCallback(() => {
    const map = new Map<HTMLElement, DOMRect>();
    rowRefs.current.forEach((el) => {
      map.set(el, el.getBoundingClientRect());
    });
    flipPendingRef.current = map;
  }, []);

  React.useLayoutEffect(() => {
    const old = flipPendingRef.current;
    if (!old) return;
    flipPendingRef.current = null;
    rowRefs.current.forEach((el, id) => {
      if (id === dragId) return;
      const oldRect = old.get(el);
      if (!oldRect) return;
      const newRect = el.getBoundingClientRect();
      const dy = oldRect.top - newRect.top;
      if (Math.abs(dy) < 0.5) return;
      try {
        el.animate(
          [
            { transform: `translateY(${dy}px)` },
            { transform: "translateY(0)" },
          ],
          {
            duration: 220,
            easing: "cubic-bezier(.2,.8,.2,1)",
            composite: "replace",
          },
        );
      } catch {
        /* WAAPI optional */
      }
    });
  });

  const orderedPatientModulesRef = React.useRef(orderedPatientModules);
  orderedPatientModulesRef.current = orderedPatientModules;

  const tryReorderPatient = React.useCallback(
    (fromId: string, overId: string, clientY: number) => {
      if (fromId === overId) return;
      const order = orderedPatientModulesRef.current.map((m) => m.id as string);
      const fromIdx = order.indexOf(fromId);
      const toIdx = order.indexOf(overId);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
      const targetEl = rowRefs.current.get(overId);
      if (targetEl) {
        const rect = targetEl.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const goingDown = fromIdx < toIdx;
        if (goingDown && clientY < midY) return;
        if (!goingDown && clientY > midY) return;
      }
      snapshotPatientPositions();
      const next = [...order];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved!);
      setPatientModulesOrder(next);
    },
    [snapshotPatientPositions],
  );

  const startPatientPointerDrag = React.useCallback(
    (id: string, ev: React.PointerEvent<HTMLElement>) => {
      if (ev.button !== 0 && ev.pointerType === "mouse") return;
      ev.preventDefault();
      setDragId(id);
      const target = ev.currentTarget;
      try {
        target.setPointerCapture(ev.pointerId);
      } catch {
        /* non-fatal */
      }
      document.body.style.userSelect = "none";

      const onMove = (e: PointerEvent) => {
        for (const [rowId, rowEl] of rowRefs.current.entries()) {
          if (rowId === id) continue;
          const rect = rowEl.getBoundingClientRect();
          if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
            tryReorderPatient(id, rowId, e.clientY);
            return;
          }
        }
      };
      const onUp = () => {
        setDragId(null);
        document.body.style.userSelect = "";
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };
      window.addEventListener("pointermove", onMove, { passive: false });
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [tryReorderPatient],
  );

  if (collapsed) {
    return (
      <div className="border-b border-sf-border px-2 pb-2 pt-2">
        <div className="relative mb-1">
          <button
            type="button"
            onClick={() => appFocusPatientOnDashboard(patient)}
            title={displayName}
            className={cn(
              "focus-ring flex h-8 w-full items-center justify-center rounded-md border-none bg-transparent px-0 font-[inherit] transition-colors hover:bg-gray-100",
              isOnPatientModule && "bg-sf-primary-50",
            )}
          >
            <PatientAvatar patient={patient} size={28} />
          </button>
          <button
            type="button"
            onClick={onClear}
            aria-label="Désélectionner le patient et masquer ses modules"
            title="Désélectionner le patient — les modules disparaîtront"
            className="absolute right-1 top-1/2 z-10 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full border border-sf-border bg-white text-sf-text-muted shadow-sm hover:bg-gray-50 hover:text-sf-text"
          >
            <I.X size={9} stroke={2} />
          </button>
        </div>
        <nav
          className="flex flex-col gap-px overflow-visible"
          aria-label={`Modules de ${displayName}`}
        >
          {PATIENT_MODULE_NAV.map((m) => {
            const Icn = (I as Record<string, IconCmp>)[m.icon] ?? I.Circle;
            return (
              <NavItem
                key={m.id}
                item={{
                  id: m.id,
                  label: m.label,
                  icon: Icn,
                  badge: null,
                }}
                active={activeModule === m.id}
                collapsed
                contained
                onClick={() => onOpenModule(m.id)}
              />
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className="border-b border-sf-border px-2 pb-2.5 pt-1.5">
      <section
        aria-label={`Dossier de ${displayName}`}
        className="overflow-hidden rounded-lg border border-sf-primary-100/70 bg-gradient-to-br from-sf-primary-50/35 via-white/50 to-white/80 shadow-[0_1px_2px_rgba(0,0,0,.04)]"
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-sf-primary-50/90 to-white px-2.5 pb-2 pt-2">
          <button
            type="button"
            onClick={onClear}
            aria-label="Désélectionner le patient et masquer ses modules"
            title="Désélectionner le patient — les modules disparaîtront"
            className="absolute right-1 top-1 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md border-none bg-white/90 text-sf-text-muted shadow-sm backdrop-blur-sm hover:bg-white hover:text-sf-text"
          >
            <I.X size={13} />
          </button>

          <button
            type="button"
            onClick={() => appFocusPatientOnDashboard(patient)}
            className="flex w-full items-center gap-2.5 py-1.5 pr-8 text-left"
          >
            <PatientAvatar patient={patient} size={40} online />
            <PatientCardIdentity patient={patient} />
          </button>

          <div className="mt-2">
            <PatientQuickActions patient={patient} />
          </div>
        </div>

        <div className="border-t border-sf-primary-100/60 bg-sf-primary-50/15 px-1.5 py-1.5">
          <nav
            className="flex flex-col gap-px"
            aria-label={`Modules de ${displayName}`}
          >
            {visiblePatientModules.map((m) => {
              const Icn = (I as Record<string, IconCmp>)[m.icon] ?? I.Circle;
              const isHidden = config.patientHiddenIds.includes(m.id);
              return (
                <PatientModuleRow
                  key={m.id}
                  id={m.id}
                  label={m.label}
                  Icon={Icn}
                  active={activeModule === m.id}
                  editMode={editMode}
                  isHidden={isHidden}
                  dragging={dragId === m.id}
                  registerRow={registerPatientRow}
                  onClick={() => onOpenModule(m.id)}
                  onToggleHide={() => togglePatientModuleVisibility(m.id)}
                  onPointerDrag={(ev) => startPatientPointerDrag(m.id, ev)}
                />
              );
            })}
          </nav>
        </div>
      </section>
    </div>
  );
}

function PatientModuleRow({
  id,
  label,
  Icon,
  active,
  editMode,
  isHidden,
  dragging,
  registerRow,
  onClick,
  onToggleHide,
  onPointerDrag,
}: {
  id: string;
  label: string;
  Icon: IconCmp;
  active: boolean;
  editMode: boolean;
  isHidden: boolean;
  dragging: boolean;
  registerRow: (id: string, el: HTMLElement | null) => void;
  onClick: () => void;
  onToggleHide: () => void;
  onPointerDrag: (ev: React.PointerEvent<HTMLElement>) => void;
}) {
  const refCb = React.useCallback(
    (el: HTMLElement | null) => registerRow(id, el),
    [registerRow, id],
  );

  if (!editMode) {
    return (
      <NavItem
        item={{ id, label, icon: Icon, badge: null }}
        active={active}
        collapsed={false}
        contained
        onClick={onClick}
      />
    );
  }

  return (
    <div
      ref={refCb}
      className={cn(
        "group relative flex h-10 w-full items-center gap-0.5 rounded-lg pl-0.5 pr-1 transition-[background-color,box-shadow] touch-none lg:h-8 lg:rounded-md",
        dragging
          ? "z-10 bg-white shadow-[0_4px_12px_-2px_rgba(79,70,229,0.25)] ring-1 ring-sf-primary/40"
          : active
            ? "bg-sf-primary-50"
            : "bg-transparent hover:bg-white/55",
        !dragging && isHidden && "opacity-60",
      )}
    >
      {dragging && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-0.5 rounded-r bg-sf-primary"
        />
      )}
      <span
        onPointerDown={onPointerDrag}
        className="flex h-6 w-3.5 shrink-0 cursor-grab touch-none items-center justify-center text-sf-text-muted/70 hover:text-sf-text-secondary active:cursor-grabbing"
        title="Glisser pour réorganiser"
      >
        <I.Drag size={12} />
      </span>
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-2 border-none bg-transparent px-1 py-1 text-left font-[inherit] text-[13px] text-sf-text"
      >
        <Icon size={14} className="shrink-0 text-sf-text-secondary" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
      </button>
      <button
        type="button"
        onClick={onToggleHide}
        title={isHidden ? "Afficher" : "Masquer"}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border-0 bg-transparent text-sf-text-muted hover:bg-gray-100 hover:text-sf-text"
      >
        {isHidden ? <I.EyeOff size={13} /> : <I.Eye size={13} />}
      </button>
    </div>
  );
}

function NavItem({
  item,
  active,
  collapsed,
  contained = false,
  onClick,
}: {
  item: Pick<NavItemData, "id" | "label" | "icon" | "badge" | "badgeColor"> & {
    icon: IconCmp;
  };
  active: boolean;
  collapsed: boolean;
  contained?: boolean;
  onClick: () => void;
}) {
  const Icn = item.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={cn(
        "relative flex h-10 w-full items-center gap-2.5 rounded-lg border-none font-[inherit] text-[14px] transition-colors lg:h-8 lg:rounded-md lg:text-[13px]",
        collapsed
          ? "justify-center px-0"
          : contained
            ? "justify-start px-2"
            : "justify-start px-2.5",
        contained
          ? active
            ? "cursor-pointer bg-sf-primary-50 font-semibold text-sf-primary"
            : cn(
                "cursor-pointer bg-transparent font-medium text-sf-text-secondary",
                collapsed
                  ? "hover:bg-gray-100 hover:text-sf-text"
                  : "hover:bg-white/55 hover:text-sf-text",
              )
          : active
            ? "cursor-pointer bg-sf-primary-50 font-semibold text-sf-primary"
            : "cursor-pointer bg-transparent font-medium text-sf-text-secondary hover:bg-gray-100",
      )}
    >
      {active && (
        <span className="absolute bottom-1.5 left-[-8px] top-1.5 w-[2.5px] rounded-sm bg-sf-primary" />
      )}
      <Icn size={16} stroke={active ? 2 : 1.75} className="shrink-0" />
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1 truncate text-left">
            {item.label}
          </span>
          {item.badge != null && (
            <span
              className={cn(
                "flex h-[18px] min-w-5 items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold",
                item.badgeColor === "primary"
                  ? "border-none bg-sf-primary text-white"
                  : "border border-sf-border bg-sf-surface-2 text-sf-text-secondary",
              )}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
      {collapsed && item.badge != null && (
        <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-sf-primary ring-2 ring-sf-surface" />
      )}
    </button>
  );
}

export { Sidebar };
